import { ObjectId } from 'app/common.types';
import { ROUTER_API } from 'app/constants/api.constant';
import BaseApi from 'app/services/api/base.api';
import axios from 'axios';
import {
  FlatLoyaltyOfferDTO,
  LoyaltyCardInDTO,
  LoyaltyCardUpdateDTO,
  LoyaltyModelDetailDTO,
  LoyaltyModelEntity,
  LoyaltyModelWithoutOfferFormDTO,
  LoyaltyModelWithoutOffersDTO,
  LoyaltyOfferDTO,
  LoyaltyOfferStatus,
  LoyaltyOffersInDTO,
  LoyaltyProduct,
  PercentageLoyaltyOfferDTO,
  ProductCatalogueImportReport,
  ProductCataloguePage,
  ProductLoyaltyOfferDTO,
  UpdateLoyaltyModelDTO,
} from '../type';

export const loyaltiesApi = new BaseApi({
  baseURL: ROUTER_API,
  tokenKey: 'token',
  timeout: 20000,
});

export const getLoyaltyModelApi = async (franchiseId: string, storeId?: string) => {
  const response = await loyaltiesApi.api.get<LoyaltyModelWithoutOffersDTO>(
    `/api/v2/franchises/${franchiseId}/loyalty-model`,
    {
      params: { storeId },
    },
  );
  return response.data;
};

export const getLoyaltyModelDetailApi = async (franchiseId: string, storeId: string) => {
  const response = await loyaltiesApi.api.get<LoyaltyModelDetailDTO>(
    `/api/v2/franchises/${franchiseId}/loyalty-model/detail`,
    {
      params: { storeId },
    },
  );
  return response.data;
};

export const getLoyaltyOffersApi = async (storeId?: string) => {
  const response = await loyaltiesApi.api.get<LoyaltyOffersInDTO>(`/api/v2/loyalties/offers`, {
    params: {
      includeArchived: true,
      includeRedeemedCount: true,
      storeId,
    },
  });
  return response.data;
};

export const createLoyaltyModelApi = async (request: LoyaltyModelWithoutOfferFormDTO) => {
  const response = await axios.post<LoyaltyModelEntity>('/api/LoyaltyModels', request);
  return response.data;
};

export const updateLoyaltyModelApi = async (request: UpdateLoyaltyModelDTO) => {
  const response = await loyaltiesApi.api.put<LoyaltyModelEntity>(`/api/v2/loyalty-models/${request.id}`, request);
  return response.data;
};

export const getLoyaltyProductListApi = async (storeId: string) => {
  const response = await loyaltiesApi.api.get<LoyaltyProduct[]>(`/api/v2/loyalties/stores/${storeId}/products`);
  return response.data;
};

export const getLoyaltyExtrasListApi = async (storeId: string) => {
  const response = await loyaltiesApi.api.get<LoyaltyProduct[]>(`/api/v2/loyalties/stores/${storeId}/extras`);
  return response.data;
};

export const updateLoyaltyProductListApi = async (storeId: string, loyaltyProductList: LoyaltyProduct[]) => {
  const response = await loyaltiesApi.api.patch<LoyaltyProduct[]>(
    `/api/v2/loyalties/stores/${storeId}/products`,
    loyaltyProductList,
  );
  return response.data;
};

export const importProductCatalogueApi = async (storeId: string, file: File) => {
  const form = new FormData();
  form.append('file', file);
  const { data } = await loyaltiesApi.api.post<ProductCatalogueImportReport>(
    `/api/v2/loyalties/stores/${storeId}/products/import`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
};

export const getProductCatalogueApi = async (storeId: string, search: string, page = 1, limit = 50) => {
  const { data } = await loyaltiesApi.api.get<ProductCataloguePage>(
    `/api/v2/loyalties/stores/${storeId}/products/catalogue`,
    { params: { search: search || undefined, page, limit } },
  );
  return data;
};

export const createOfferApi = async (
  loyaltyModelId: LoyaltyModelEntity['id'],
  offer: Partial<FlatLoyaltyOfferDTO | PercentageLoyaltyOfferDTO | ProductLoyaltyOfferDTO>,
) => {
  const response = await loyaltiesApi.api.post<LoyaltyOfferDTO>(
    `/api/v2/loyalty-models/${loyaltyModelId}/offers`,
    offer,
  );
  return response.data;
};

export const updateOfferApi = async (
  loyaltyModelId: LoyaltyModelEntity['id'],
  loyaltyOfferId: LoyaltyOfferDTO['id'],
  offer: Partial<FlatLoyaltyOfferDTO | PercentageLoyaltyOfferDTO | ProductLoyaltyOfferDTO>,
) => {
  const response = await loyaltiesApi.api.put<LoyaltyOfferDTO>(
    `/api/v2/loyalty-models/${loyaltyModelId}/offers/${loyaltyOfferId}`,
    offer,
  );
  return response.data;
};

export const changeOfferStatusApi = async (
  loyaltyModelId: string,
  loyaltyOfferId: string,
  status: LoyaltyOfferStatus,
) => {
  const response = await loyaltiesApi.api.post<LoyaltyOfferDTO>(
    `/api/v2/loyalty-models/${loyaltyModelId}/offers/${loyaltyOfferId}/status`,
    {
      status,
    },
  );
  return response.data;
};

export const patchLoyaltyCardNoteApi = async (
  userId: ObjectId,
  franchiseId: ObjectId,
  loyaltyCardId: ObjectId,
  update: LoyaltyCardUpdateDTO,
) => {
  const response = await loyaltiesApi.api.patch<LoyaltyCardInDTO>(
    `api/v2/users/${userId}/loyalty-cards/${loyaltyCardId}`,
    update,
    { params: { franchiseId } },
  );
  return response.data;
};
